import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  IconButton,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { cloneDeep, isEqual, omit } from 'lodash';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Row } from 'react-table';
import { uuid } from 'short-uuid';

import { Table, useDialogs } from '@work4all/components';
import { Card } from '@work4all/components/lib/dataDisplay/card';

import { TravelReceipts } from '@work4all/models/lib/Classes/TravelReceipts.entity';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { formatCurrency, formatNumberAsCurrency } from '@work4all/utils';

import { MaskDialogOverlay } from '../../../../../../components/entity-picker/MaskDialogOverlay';
import { useFormContextPlus } from '../../../../form-plus/use-form-context-plus';
import { Collapse } from '../../../components';
import { CurrencyExchangeInfoContext } from '../../inbound-invoice/currency-exchange-info-context';
import { TravelExpensesFormValue } from '../type';

import { getTravelReceiptsTableColumns } from './travel-receipts-table-config';
import { TravelReceiptsCardView } from './TravelReceiptsCardView';

export const TravelReceiptsTable = () => {
  const { t } = useTranslation();

  const exchangeInfoContext = useContext(CurrencyExchangeInfoContext);

  const isViewportDownMd = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md')
  );

  const { watch, setValue, getValues } =
    useFormContextPlus<TravelExpensesFormValue>();

  const rawData = watch('recepitsEdited');

  const data = useMemo(() => {
    return rawData || [];
  }, [rawData]);

  // Calculate total sum from all receipts
  const receiptsTotal = useMemo(() => {
    return data.reduce((acc, receipt) => {
      return acc + (receipt.expenditure || 0);
    }, 0);
  }, [data]);

  const shouldShowTotalCurrency = useMemo(() => {
    const firstCurrency = data[0]?.currency?.name;
    return data.every((receipt) => receipt.currency?.name === firstCurrency);
  }, [data]);

  const columns = useMemo(() => {
    return getTravelReceiptsTableColumns(t);
  }, [t]);

  const [selectedRows, setSelectedRows] = useState<
    { original: TravelReceipts }[]
  >([]);

  const onSelectedRowsChange = useCallback((rows: Row[]) => {
    setSelectedRows((old) => {
      if (isEqual(old, rows)) {
        return old;
      }
      return rows;
    });
  }, []);

  const dialogs = useDialogs();

  // Extract edit logic into reusable callback
  const handleEditReceipt = useCallback(
    (receipt: TravelReceipts) => {
      const receiptId = receipt.id;

      const dialog = dialogs.open(MaskDialogOverlay, {
        entity: Entities.travelReceipts,
        onAfterSave: onAfterSave,
        close: closeMaskOverlay,
        params: {
          skipPersit: 'true',
          initialData: JSON.stringify(omit(receipt, 'id')),
        },
      });

      function onAfterSave(data) {
        const receipts = getValues('recepitsEdited') || [];
        const originalReceipts = getValues('receipts') || [];

        const original = originalReceipts.find((r) => r.id === receiptId);
        const oldFiles = original?.file || [];
        const newFiles = data.tempFileManagerList || [];

        // Calculate what was added (new files not in old files)
        const attachmentsToAdd = newFiles
          .filter(
            (newFile) => !oldFiles.some((oldFile) => oldFile.id === newFile.id)
          )
          .map((attachment) => ({
            tempFileId: attachment.id as string,
            name: attachment.fileName || '',
          }));

        // Calculate what was removed (old files not in new files)
        const attachmentsToRemove = oldFiles
          .filter(
            (oldFile) => !newFiles.some((newFile) => newFile.id === oldFile.id)
          )
          .map((attachment) => attachment.id as number);

        data.data.attachements = {
          add: attachmentsToAdd,
          remove: attachmentsToRemove,
        };

        data.data.file = data.tempFileManagerList;

        setValue(
          'recepitsEdited',
          receipts.map((existingReceipt) => {
            if (existingReceipt.id === receiptId) {
              const newReceipt = cloneDeep(existingReceipt);
              Object.entries(data.data).forEach(([key, value]) => {
                newReceipt[key] = value;
              });
              return newReceipt;
            }
            return existingReceipt;
          }),
          {
            shouldValidate: true,
            shouldDirty: true,
          }
        );
        dialogs.close(dialog.id);
      }

      function closeMaskOverlay() {
        dialogs.close(dialog.id);
      }
    },
    [dialogs, getValues, setValue]
  );

  // Helper functions for card view
  const selectedRowIds = useMemo(() => {
    return selectedRows.map((row) =>
      String((row.original as TravelReceipts).id)
    );
  }, [selectedRows]);

  const handleCardRowSelection = useCallback(
    (id: string, selected: boolean) => {
      const receipt = data.find((item) => String(item.id) === id);
      if (!receipt) return;

      if (selected) {
        // Add to selection
        const newRow = { original: receipt };
        setSelectedRows((prev) => [...prev, newRow]);
      } else {
        // Remove from selection
        setSelectedRows((prev) =>
          prev.filter(
            (row) => String((row.original as TravelReceipts).id) !== id
          )
        );
      }
    },
    [data]
  );

  const handleCardDoubleClick = useCallback(
    (receipt: TravelReceipts) => {
      handleEditReceipt(receipt);
    },
    [handleEditReceipt]
  );

  return (
    <Collapse
      title={t('COMMON.TRAVELRECEIPTS')}
      defaultOpen
      headerBar={
        <Stack direction="row" mx={0.5}>
          <IconButton
            color="primary"
            onClick={() => {
              const dialog = dialogs.open(MaskDialogOverlay, {
                entity: Entities.travelReceipts,
                params: {
                  skipPersit: 'true',
                },
                onAfterSave: onAfterSave,
                close: closeMaskOverlay,
              });
              function onAfterSave(data) {
                const receipts = getValues('recepitsEdited') || [];

                // create temporary id
                data.data.id = uuid();
                // add attachements
                data.data.attachements = data.relations.attachements;
                data.data.file = data.tempFileManagerList;

                setValue('recepitsEdited', [...receipts, data.data], {
                  shouldValidate: true,
                  shouldDirty: true,
                });
                dialogs.close(dialog.id);
              }
              function closeMaskOverlay() {
                dialogs.close(dialog.id);
              }
            }}
          >
            <AddIcon />
          </IconButton>
          {selectedRows.length > 0 && (
            <>
              {selectedRows.length === 1 && (
                <IconButton
                  color="primary"
                  onClick={() => {
                    const selectedReceipt = selectedRows[0]
                      .original as TravelReceipts;
                    handleEditReceipt(selectedReceipt);
                  }}
                >
                  <EditIcon />
                </IconButton>
              )}

              <IconButton
                color="primary"
                onClick={async () => {
                  const confirmed = await dialogs.confirm({
                    title: t('ALERTS.HINT'),
                    description: t('ALERTS.DELETE.REASSURE', {
                      count: selectedRows.length,
                    }),
                    confirmLabel: t('ALERTS.BTN_DELETE'),
                    cancelLabel: t('ALERTS.BTN_ABORT'),
                  });

                  if (confirmed) {
                    const selectedReceipts = selectedRows.map(
                      (row) => row.original
                    );

                    // Remove selected receipts from receipts array (all selected receipts regardless of ID type)
                    const currentReceipts = getValues('recepitsEdited') || [];
                    const selectedIds = selectedReceipts.map(
                      (receipt) => (receipt as TravelReceipts).id
                    );
                    const updatedReceipts = currentReceipts.filter(
                      (receipt) =>
                        !selectedIds.includes((receipt as TravelReceipts).id)
                    );

                    // Update receipts in form
                    setValue('recepitsEdited', updatedReceipts, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });

                    // Clear selection
                    setSelectedRows([]);
                  }
                }}
              >
                <DeleteIcon />
              </IconButton>
            </>
          )}
        </Stack>
      }
    >
      <Card>
        {isViewportDownMd ? (
          <TravelReceiptsCardView
            data={data}
            selectedRows={selectedRowIds}
            onRowSelection={handleCardRowSelection}
            onClick={handleCardDoubleClick}
          />
        ) : (
          <Table
            columns={columns}
            data={data}
            mode="client"
            allItemsCount={data.length}
            onSelectedRowsChange={onSelectedRowsChange}
            onRowDoubleClick={(id: string) => {
              const receipt = data.find(
                (item) => String(item.id) === id
              ) as TravelReceipts;
              if (receipt) {
                handleEditReceipt(receipt);
              }
            }}
          />
        )}
        <div
          style={{
            padding: '16px',
            borderTop: '1px solid #e0e0e0',
            textAlign: 'right',
          }}
        >
          <Typography variant="body1" fontWeight={700}>
            {`${t('COMMON.TOTAL')}: ${formatNumberAsCurrency(receiptsTotal)}${
              shouldShowTotalCurrency && exchangeInfoContext?.defaultCurrency
                ? ` ${formatCurrency(exchangeInfoContext.defaultCurrency, t)}`
                : ''
            }`}
          </Typography>
        </div>
      </Card>
    </Collapse>
  );
};
